<template>
  <transition name="auth-modal-fade">
    <div v-if="isOpen" class="auth-modal" role="dialog" aria-modal="true">
      <div class="auth-modal__scrim" @click="close"></div>

      <div ref="panel" class="auth-modal__panel" @keydown.tab="trapFocus">
        <span class="auth-modal__grabber"></span>

        <AuthModalHeader :show-back="canGoBack" @back="goBack" @close="close" />

        <div class="auth-modal__body">
          <component
            :is="activeComponent"
            ref="view"
            :key="view"
            v-bind="viewProps"
            @back-state="canGoBack = $event"
            @switch-view="switchView"
            @flash="flashMessage = $event"
            @success="handleSuccess" />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
  import AuthModalHeader from './AuthModalHeader.vue';
  import LoginView from './views/LoginView.vue';
  import RegisterView from './views/RegisterView.vue';
  import ForgotPasswordView from './views/ForgotPasswordView.vue';
  import {
    AUTH_VIEW,
    getAuthViewFromRoute,
    getAuthOptions,
    resetAuthOptions,
    setAuthView,
    closeAuthModal,
    finishAuthModal
  } from '@/utils/authModal.js';

  const COMPONENT_BY_VIEW = {};
  COMPONENT_BY_VIEW[AUTH_VIEW.LOGIN] = 'LoginView';
  COMPONENT_BY_VIEW[AUTH_VIEW.REGISTER] = 'RegisterView';
  COMPONENT_BY_VIEW[AUTH_VIEW.FORGOT_PASSWORD] = 'ForgotPasswordView';

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  export default {
    name: 'AuthModal',
    components: {
      AuthModalHeader,
      LoginView,
      RegisterView,
      ForgotPasswordView
    },
    data() {
      return {
        canGoBack: false,
        flashMessage: '',
        previouslyFocused: null,
        previousOverflow: null
      }
    },
    computed: {
      view() {
        return getAuthViewFromRoute(this.$route);
      },
      isOpen() {
        return !!this.view;
      },
      activeComponent() {
        return COMPONENT_BY_VIEW[this.view] || COMPONENT_BY_VIEW[AUTH_VIEW.LOGIN];
      },
      viewProps() {
        return this.view === AUTH_VIEW.LOGIN ? { message: this.flashMessage } : {};
      }
    },
    watch: {
      isOpen: {
        immediate: true,
        handler(isOpen) {
          if (isOpen) {
            this.flashMessage = this.readOptionMessage();
            this.lockScroll();
            this.captureFocus();
            return;
          }
          this.canGoBack = false;
          this.flashMessage = '';
          resetAuthOptions();
          this.unlockScroll();
          this.restoreFocus();
        }
      },
      view() {
        this.canGoBack = false;
      }
    },
    mounted() {
      document.addEventListener('keydown', this.handleEscape);
    },
    beforeDestroy() {
      document.removeEventListener('keydown', this.handleEscape);
      this.unlockScroll();
    },
    methods: {
      readOptionMessage() {
        const key = getAuthOptions().message;
        return key ? this.$t(key) : '';
      },
      switchView(view) {
        setAuthView(view);
      },
      goBack() {
        const view = this.$refs.view;
        if (view && typeof view.goBack === 'function') {
          view.goBack();
          return;
        }
        this.close();
      },
      close() {
        closeAuthModal();
      },
      handleSuccess() {
        finishAuthModal();
      },
      handleEscape(event) {
        if (!this.isOpen) return;
        if (event.key !== 'Escape' && event.keyCode !== 27) return;
        this.close();
      },
      /* a drawer may already own the lock, so remember what was there */
      lockScroll() {
        this.previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
      },
      unlockScroll() {
        if (this.previousOverflow === null) return;
        document.body.style.overflow = this.previousOverflow;
        this.previousOverflow = null;
      },
      captureFocus() {
        this.previouslyFocused = document.activeElement;
        this.$nextTick(() => {
          const panel = this.$refs.panel;
          if (!panel) return;
          const focusable = panel.querySelectorAll(FOCUSABLE);
          if (focusable.length > 0) focusable[0].focus();
        });
      },
      restoreFocus() {
        if (!this.previouslyFocused) return;
        if (typeof this.previouslyFocused.focus === 'function') this.previouslyFocused.focus();
        this.previouslyFocused = null;
      },
      trapFocus(event) {
        const panel = this.$refs.panel;
        if (!panel) return;

        const focusable = Array.prototype.slice.call(panel.querySelectorAll(FOCUSABLE));
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
          return;
        }
        if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  }
</script>

<style scoped>
.auth-modal {
  position: fixed;
  inset: 0;
  z-index: 1070;
}
.auth-modal__scrim {
  position: absolute;
  inset: 0;
  background: rgba(16, 24, 40, 0.55);
  backdrop-filter: blur(2px);
}
.auth-modal__panel {
  position: absolute;
  z-index: 1071;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 32px);
  max-width: 440px;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 24px 60px rgba(16, 24, 40, 0.18);
  animation: authCardIn .16s ease-out;
}
.auth-modal__grabber {
  display: none;
}
.auth-modal__body {
  padding: 0 24px 24px;
}
@keyframes authCardIn {
  from { opacity: 0; transform: translate(-50%, calc(-50% + 8px)); }
  to { opacity: 1; transform: translate(-50%, -50%); }
}

.auth-modal-fade-enter-active,
.auth-modal-fade-leave-active {
  transition: opacity .16s ease;
}
.auth-modal-fade-enter,
.auth-modal-fade-leave-to {
  opacity: 0;
}

@media (max-width: 575.98px) {
  .auth-modal__panel {
    left: 0;
    top: auto;
    bottom: 0;
    width: 100%;
    max-width: none;
    max-height: 92vh;
    transform: none;
    border-radius: 24px 24px 0 0;
    padding-top: 12px;
    box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.18);
    animation: authSheetUp .25s ease-out;
  }
  .auth-modal__grabber {
    display: block;
    width: 40px;
    height: 4px;
    margin: 0 auto;
    background: #E0E8EF;
    border-radius: 99px;
  }
  .auth-modal__body {
    padding: 0 20px 24px;
  }
  @keyframes authSheetUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/v2/auth/AuthModal.vue